import React, { Fragment, useCallback, useEffect, useState } from "react";
import commonStyles from "@styles/CommonPageLayout.module.css";
import { useNavigate, useSearchParams } from "react-router-dom";
import Button from "@components/buttons/Button/Button";
import { fetchMemorial, setMemorialAdmin } from "@services/memorialService";
import { Card } from "@mui/material";
import styles from "@styles/CommonEditorPageLayout.module.css";
import TextInput from "@components/inputs/TextInput/TextInput";
import { isValidEmail } from "@util/validators";
import { Memorial } from "@models/Memorial";
import LoadSaveErrorDisplay from "@components/display/LoadSaveErrorDisplay/LoadSaveErrorDisplay";

const SetAdminPage: React.FC = () => {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const id = searchParams.get("id");
    const mode = searchParams.get("mode");
    const [memorial, setMemorial] = useState<Memorial>();

    const [error, setError] = useState<string | null>(null);
    const [loading, setLoading] = useState(true);
    const [isSaving, setIsSaving] = useState(false);

    const [email, setEmail] = useState("");
    const [emailError, setEmailError] = useState("");
    const [submitErrorMessage, setSubmitErrorMessage] = useState("");

    useEffect(() => {
        const fetchData = async () => {
            try {
                setLoading(true);
                if (!id) {
                    throw new Error("No ID.");
                }
                const data = await fetchMemorial(id);
                setMemorial(data);
            } catch (err) {
                setError("Failed to fetch memorial info.");
            } finally {
                setLoading(false);
            }
        };

        void fetchData();
    }, [id]);

    const validateEmail = (val: string) => {
        return isValidEmail(val);
    };

    // Handlers for updating address fields and performing validation
    const handleEmailChange = useCallback((val: string) => {
        setEmail(val);
        setEmailError(validateEmail(val));
    }, []);

    const handleSubmit = async () => {
        let isValid = true;

        // Validate Street Address
        const errorMessage = validateEmail(email);
        setEmailError(errorMessage);
        if (errorMessage) isValid = false;

        if (isValid) {
            try {
                setIsSaving(true);
                setSubmitErrorMessage("");

                if (!id) {
                    throw new Error("No ID.");
                }

                await setMemorialAdmin(id, email);

                setIsSaving(false);
                navigate("../set-admin-success");
            } catch (err) {
                setSubmitErrorMessage("Failed to set admin. Please try again.");
            } finally {
                setIsSaving(false);
            }
        }
    };

    const handleCancel = async () => {
        navigate(-1);
    };

    const renderContent = () => {
        if (loading || isSaving || error) {
            return <LoadSaveErrorDisplay isLoading={loading} isSaving={isSaving} error={error} />;
        }

        if (!memorial) {
            return <div className={`${commonStyles.noContent} ${commonStyles.note}`}>No Plaque info found.</div>;
        }

        return (
            <>
                <h1>{`${memorial.firstName} ${memorial.lastName}`}</h1>
                <h2>Set Page Admin</h2>
                <div className={styles.contentContainer}>
                    <Card className={styles.cardContainer}>
                        <TextInput
                            label="Admin Email *"
                            id="email"
                            value={email}
                            onChange={handleEmailChange}
                            placeholder="Enter email..."
                            errorMessage={emailError}
                        />
                        <div className={styles.formButtons}>
                            <Button
                                variant="text"
                                onClick={handleCancel}
                            >
                                Cancel
                            </Button>
                            <Button
                                variant="primary"
                                onClick={handleSubmit}
                            >
                                Save
                            </Button>
                        </div>
                        {submitErrorMessage && (
                            <p className={styles.errorMessage}>{submitErrorMessage}</p>
                        )}
                    </Card>
                    <div className={styles.messageContainer}>
                        {mode === "change" && (
                            <>
                                <p className={styles.errorMessage}>Note: There is already an existing pending invitation.</p>
                                <p className={styles.errorMessage}>Setting new page admin will cancel the pending invitation</p>
                            </>
                        )}
                        <p>Once page admin is set and accepted, it cannot be changed.</p>
                        <p>You will retain access to edit the memorial.</p>
                    </div>
                </div>
            </>
        );
    };

    return (
        <div className={commonStyles.container}>
            {renderContent()}
        </div>
    );
};

export default SetAdminPage;
